import React, { Fragment } from "react";

import styled from "styled-components";
import { Flex } from "../../../../components/Flex";
import { Text } from "../../../../components/Text";
import { linkify } from "../../../../utils/String";
import { AtiraIcon } from "../../../../components/AtiraIcon";
import { Rounded } from "../../../../theme/Rounded";
import { standardDate } from "../../../../utils/Date";
import { Shadow } from "../../../../theme/Shadow";
import { Spacing } from "../../../../theme/Spacing";
import { faBan } from "@fortawesome/free-solid-svg-icons/faBan";
import { faBullhorn } from "@fortawesome/free-solid-svg-icons/faBullhorn";
import { faReply } from "@fortawesome/free-solid-svg-icons/faReply";
import { faRobot } from "@fortawesome/free-solid-svg-icons/faRobot";
import { useTranslation } from "react-i18next";
import { ConversationMessage } from "../../../../model/integration/dto/meta/MetaConversationMessage";
import { faCheckDouble } from "@fortawesome/free-solid-svg-icons/faCheckDouble";
import { WAMetaMessageDirection } from "../../../../model/integration/types/whatsapp/WAMetaMessageDirection.enum";
import { Tooltip } from "../../../../utils/local-antd";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons/faInfoCircle";
import { WAMessageImage } from "./components/WAMessageImage";
import { WAMessageSticker } from "./components/WAMessageSticker";
import { WAAudioMessage } from "./components/WAAudioMessage";
import { WAVideoMessage } from "./components/WAVideoMessage";
import { WAMessageDocument } from "./components/WAMessageDocument";
import { WAMessageContact } from "./components/WAMessageContact";
import { WAMessageLocation } from "./components/WAMessageLocation";
import { WAMessageTemplate } from "./components/WAMessageTemplate";
import { WAMessageAvatar } from "./components/WAMessageAvatar";
import { WAMessageTicks } from "./components/WAMessageTicks";
import { WAMessageMenu } from "./WAMessageMenu";
import { WAMessageReactions } from "./WAMessageReactions";
import { WAMessageReply } from "./WAMessageReply";
import { useTheme } from "../../../../ThemeContext";

const Row = styled.div<{ $isSender: boolean }>`
  display: flex;
  align-items: flex-end;
  justify-content: ${({ $isSender }) =>
    $isSender ? "flex-end" : "flex-start"};
  gap: ${Spacing.xs};
  width: 100%;
  min-width: 0;
`;

const AvatarSlot = styled.div`
  flex-shrink: 0;
  margin-bottom: ${Spacing.xs};
`;

const Container = styled(Flex)<{
  $isSender: boolean;
  $hasReactions: boolean;
}>`
  flex-direction: column;
  align-items: stretch;
  background-color: ${({ $isSender, theme }) =>
    $isSender ? theme.main : theme.white};
  color: ${({ $isSender, theme }) =>
    $isSender ? theme.white : theme.textColor};
  border: 1px solid
    ${({ $isSender, theme }) => ($isSender ? theme.main : theme.sub)};
  padding: ${Spacing.s} ${Spacing.m};
  border-radius: ${({ $isSender }) =>
    $isSender
      ? `${Rounded.lg} ${Rounded.lg} ${Rounded.sm} ${Rounded.lg}`
      : `${Rounded.lg} ${Rounded.lg} ${Rounded.lg} ${Rounded.sm}`};
  width: fit-content;
  min-width: 5rem;
  max-width: min(32rem, 72%);
  gap: ${Spacing.xs};
  box-shadow: ${Shadow.SM};
  position: relative;
  overflow: visible;

  margin-bottom: ${({ $hasReactions }) =>
    $hasReactions ? Spacing.m : Spacing.xs};
  scroll-margin: ${Spacing.xxl};
  transition:
    background-color 0.4s ease,
    outline-color 0.4s ease;

  /* An outline rather than a background tint: the bubble already has a colour,
     and on the sender's purple one a tint is all but invisible. This reads on
     both sides and does not shift the layout. */
  &.atira-wa-message--flash {
    outline: 3px solid ${({ theme }) => theme.main};
    outline-offset: 3px;
  }

  &:hover .atira-wa-message-menu {
    opacity: 1;
    pointer-events: auto;
  }

  /* No hover on touch - keep the message's action trigger reachable. */
  @media (hover: none) {
    .atira-wa-message-menu {
      opacity: 1;
      pointer-events: auto;
    }
  }
`;

const MessageText = styled(Text)`
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  line-height: 1.4;
  unicode-bidi: plaintext;
  text-align: start;

  a {
    color: inherit;
    text-decoration: underline;
  }
`;

const BubbleContent = styled(Flex)`
  flex-direction: column;
  min-width: 0;
  color: inherit;

  p,
  svg,
  a {
    color: inherit;
  }

  img,
  video {
    max-width: 100%;
    border-radius: ${Rounded.md};
  }
`;

const MetaRow = styled(Flex)<{ $isSender: boolean }>`
  align-items: center;
  align-self: flex-end;
  gap: ${Spacing.xs};
  opacity: 0.75;
  white-space: nowrap;
`;

type Props = {
  message: ConversationMessage;
  repliedTo?: ConversationMessage;
  onReply?: (message: ConversationMessage) => void;
  /** Scrolls to the quoted message, fetching history if it is not loaded */
  onJumpToMessage?: (metaMessageId: string) => void;
  /** Named on hover of the avatar beside the customer's messages */
  customerName?: string;
  /** Named on hover of the avatar beside the AI agent's messages */
  agentName?: string;
};

export const waMessageDomId = (id: string) => `atira-wa-message-${id}`;

const WhatsAppMessageComponent: React.FC<Props> = ({
  message,
  repliedTo,
  onReply,
  onJumpToMessage,
  customerName,
  agentName,
}) => {
  const { t, i18n } = useTranslation();

  const isSender = message.direction === WAMetaMessageDirection.OUTGOING;
  const reactions = message.reactions || [];
  const deleted = Boolean(message.deletedAt);
  const { theme } = useTheme();

  const jumpToRepliedTo = () => {
    if (message.context?.id) {
      onJumpToMessage?.(message.context.id);
    }
  };

  const customLinkify = (text: string) => {
    if (!text) return text;

    const linkifiedText = linkify(text);
    return linkifiedText;
  };

  const renderMessageContent = () => {
    if (deleted) {
      return (
        <Flex alignItems="center" gap="xs">
          <AtiraIcon icon={faBan} size="sm" />
          <MessageText
            fontSize="m"
            style={{ fontStyle: "italic", color: theme.darkerSub }}
          >
            {t("common.whatsapp.deleted")}
          </MessageText>
        </Flex>
      );
    }

    if ("audio" === message.type) {
      return <WAAudioMessage message={message} />;
    }

    if ("document" === message.type) {
      return <WAMessageDocument message={message} />;
    }

    if ("video" === message.type) {
      return <WAVideoMessage message={message} />;
    }

    if ("image" === message.type) {
      return <WAMessageImage message={message} />;
    }

    // Already downloaded and stored like any other media - it just had no
    // branch here, so a sticker arrived as an empty bubble
    if ("sticker" === message.type) {
      return <WAMessageSticker message={message} />;
    }

    if ("contacts" === message.type) {
      return <WAMessageContact message={message} />;
    }

    if ("location" === message.type) {
      return <WAMessageLocation message={message} />;
    }

    // A button or list the customer tapped on something we sent. This used to
    // render as "Call log", which it never was.
    const tapped =
      message.interactive?.button_reply?.title ||
      message.interactive?.list_reply?.title ||
      message.button?.text;

    if (tapped) {
      return (
        <Flex alignItems="center" gap="xs">
          <AtiraIcon
            icon={faReply}
            size="sm"
            color={isSender ? "white" : "main"}
          />
          <MessageText
            fontSize="m"
            wordBreak="break-word"
            color={isSender ? "white" : "textColor"}
          >
            {tapped}
          </MessageText>
        </Flex>
      );
    }

    if ("interactive" === message.type || "button" === message.type) {
      return (
        <MessageText fontSize="m" color={isSender ? "white" : "textColor"}>
          {t("common.whatsapp.interactive")}
        </MessageText>
      );
    }

    if ("template" === message.type) {
      return <WAMessageTemplate message={message} />;
    }

    // A type with no branch above and no text used to fall through to an empty
    // bubble - which reads as a message that isn't there, and can be replied to.
    // Name it instead, so anything WhatsApp adds later still says something.
    if (!message.text?.body) {
      const kind = message.unsupported?.type;
      const named = Boolean(
        kind && i18n.exists(`common.whatsapp.unsupported_${kind}`),
      );

      const label = named
        ? t(`common.whatsapp.unsupported_${kind}`)
        : t(`common.whatsapp.${message.type}`, {
            defaultValue: t("common.whatsapp.unsupported"),
          });

      // Meta never sends what a poll says, only that it was one - so the agent
      // is pointed at the phone, which has it, rather than at Meta's reason.
      const reason = named
        ? t("common.whatsapp.unsupported_openOnPhone")
        : typeof message.messageErrors === "string"
          ? message.messageErrors
          : null;

      return (
        <Flex alignItems="center" gap="xs">
          <AtiraIcon
            icon={faInfoCircle}
            size="sm"
            color={isSender ? "white" : "darkerSub"}
          />
          <MessageText fontSize="m" color={isSender ? "white" : "textColor"}>
            {reason ? `${label} — ${reason}` : label}
          </MessageText>
        </Flex>
      );
    }

    return (
      <MessageText
        fontSize="m"
        wordBreak="break-word"
        color={isSender ? "white" : "textColor"}
        dangerouslySetInnerHTML={{
          __html: customLinkify(message.text.body),
        }}
      />
    );
  };

  const getMessageStatus = (message: ConversationMessage) => {
    // Read gets the accent tick, everything else inherits the bubble's colour
    const tint = isSender ? "white" : "darkerSub";

    if (message.direction === WAMetaMessageDirection.INCOMING) {
      return <AtiraIcon icon={faCheckDouble} color={tint} size="xs" />;
    }

    // The only status with something to say beyond its icon
    if (message.status === "failed") {
      return (
        <Flex>
          <Tooltip
            title={message.messageErrors || t("common.whatsapp.messageFailed")}
          >
            <AtiraIcon icon={faInfoCircle} color="danger" />
            <span />
          </Tooltip>
        </Flex>
      );
    }

    return <WAMessageTicks status={message.status} color={tint} />;
  };

  return (
    <Row $isSender={isSender}>
      {!isSender ? (
        <AvatarSlot>
          <WAMessageAvatar
            message={message}
            customerName={customerName}
            agentName={agentName}
          />
        </AvatarSlot>
      ) : null}

      <Container
        $isSender={isSender}
        $hasReactions={reactions.length > 0}
        id={waMessageDomId(message._id)}
        data-meta-message-id={message.metaMessageId}
        key={message._id}
      >
        {/* Nothing left to reply to, react to or delete */}
        {!message.pending && !deleted ? (
          <WAMessageMenu
            message={message}
            isSender={isSender}
            onReply={onReply}
          />
        ) : null}

        {message.context?.id && !deleted ? (
          <WAMessageReply
            repliedTo={repliedTo}
            onJump={jumpToRepliedTo}
            jumpable={Boolean(onJumpToMessage)}
          />
        ) : null}

        <BubbleContent>{renderMessageContent()}</BubbleContent>

        <MetaRow $isSender={isSender}>
          {message.sentByAI ? (
            <Fragment>
              <AtiraIcon
                icon={faRobot}
                color={isSender ? "white" : "darkerSub"}
                size="xs"
              />
              <Text fontSize="xs" color={isSender ? "white" : "darkerSub"}>
                {t("integration.whatsapp.meta.aiAgent.messageBadge")}
              </Text>
            </Fragment>
          ) : null}

          {message.campaignId ? (
            <Fragment>
              <AtiraIcon
                icon={faBullhorn}
                color={isSender ? "white" : "darkerSub"}
                size="xs"
              />
              <Text fontSize="xs" color={isSender ? "white" : "darkerSub"}>
                {message.campaign?.name ||
                  t("integration.whatsapp.meta.campaign.messageBadge")}
              </Text>
            </Fragment>
          ) : null}

          {message.editedAt && !deleted ? (
            <Text fontSize="xs" color={isSender ? "white" : "darkerSub"}>
              {t("common.whatsapp.edited")}
            </Text>
          ) : null}

          <Text fontSize="xs" color={isSender ? "white" : "darkerSub"}>
            {standardDate<string>(
              new Date(Number(message.timestamp) * 1000),
              undefined,
              true,
            )}
          </Text>

          {isSender && !deleted ? getMessageStatus(message) : null}
        </MetaRow>

        <WAMessageReactions message={message} isSender={isSender} />
      </Container>

      {isSender ? (
        <AvatarSlot>
          <WAMessageAvatar
            message={message}
            customerName={customerName}
            agentName={agentName}
          />
        </AvatarSlot>
      ) : null}
    </Row>
  );
};

/** The thread re-renders on every socket event, and each bubble is not cheap.
 * Memoised so an incoming message does not re-render the other forty-nine.
 */
export const WhatsAppMessage = React.memo(WhatsAppMessageComponent);
